/**
 * Referrals-tab aggregates: conversion funnel (via Schedule Events),
 * internal/self-referral filter, source mix, and lag times.
 * Mirrors ReferralConversionReport.cs matching rules on the client.
 */

import { DIRECTION_INBOUND, rowMatchesFacilityScope } from '../../utils/referralDirection';
import { isQuickReferral } from '../../utils/referralTypeLabel';

export const DEFAULT_CONSULT_ALIASES = [
  'Consult',
  'Initial Consult',
  'Clinic Visit',
  'Clinic',
];

export const DEFAULT_PROCEDURE_ALIASES = [
  'Procedure',
  'Case',
  'OR Date',
];

const ALIAS_STORAGE_KEY = 'overview.referralEventAliases';

export function loadStoredAliases() {
  try {
    const raw = sessionStorage.getItem(ALIAS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      consult: Array.isArray(parsed.consult) ? parsed.consult : null,
      procedure: Array.isArray(parsed.procedure) ? parsed.procedure : null,
    };
  } catch {
    return null;
  }
}

export function saveStoredAliases(consult, procedure) {
  try {
    sessionStorage.setItem(
      ALIAS_STORAGE_KEY,
      JSON.stringify({ consult, procedure })
    );
  } catch { /* ignore */ }
}

function f(record) {
  return record?.data || record?.Data || {};
}

function normalizeId(raw) {
  if (raw == null || raw === '') return '';
  const n = Number(raw);
  if (Number.isFinite(n)) return String(Math.round(n));
  return String(raw).trim();
}

function asFacilityId(raw) {
  return normalizeId(raw);
}

export function isSelfReferral(data) {
  const creatingId = asFacilityId(data.creating_facility_id);
  const receivingId = asFacilityId(data.receiving_facility_id);
  if (creatingId && receivingId) {
    return creatingId === receivingId;
  }
  const a = String(data.creating_facility || '').trim();
  const b = String(data.receiving_facility || '').trim();
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

function matchesAlias(eventName, aliases) {
  if (!eventName || !aliases?.length) return false;
  const name = String(eventName).trim();
  if (!name) return false;
  const lower = name.toLowerCase();
  for (const alias of aliases) {
    const a = String(alias || '').trim();
    if (!a) continue;
    if (lower === a.toLowerCase()) return true;
    if (containsWholeWord(name, a)) return true;
  }
  return false;
}

function containsWholeWord(haystack, needle) {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  let idx = 0;
  while (idx <= h.length - n.length) {
    const found = h.indexOf(n, idx);
    if (found < 0) return false;
    const beforeOk = found === 0 || !/[a-z0-9]/i.test(haystack[found - 1]);
    const after = found + n.length;
    const afterOk = after >= haystack.length || !/[a-z0-9]/i.test(haystack[after]);
    if (beforeOk && afterOk) return true;
    idx = found + 1;
  }
  return false;
}

function parseEventDate(data) {
  const ed = data.event_date;
  let iso = null;
  if (ed && typeof ed === 'object' && ed.value) iso = ed.value;
  else if (typeof ed === 'string') iso = ed;
  if (!iso) {
    const cd = data.created_date;
    if (cd && typeof cd === 'object' && cd.value) iso = cd.value;
    else if (typeof cd === 'string') iso = cd;
  }
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = data.event_time;
  if (time && typeof time === 'string' && d.getUTCHours() === 0 && d.getUTCMinutes() === 0) {
    const parts = time.split(':').map(Number);
    if (parts.length >= 2 && parts.every(Number.isFinite)) {
      d.setUTCHours(parts[0], parts[1], parts[2] || 0, 0);
    }
  }
  return d;
}

function recordReceivedAt(record) {
  const data = f(record);
  const ts = record.timestamp || data.created_date;
  if (!ts) return null;
  if (typeof ts === 'object' && ts.value) {
    const d = new Date(ts.value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(String(ts));
  return Number.isNaN(d.getTime()) ? null : d;
}

function referralId(record) {
  const data = f(record);
  return normalizeId(data.referral_id ?? data.ReferralId ?? record.id);
}

function daysBetween(from, to) {
  if (!from || !to) return null;
  return (to.getTime() - from.getTime()) / 86400000;
}

function average(values) {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function median(values) {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function filterScopedReferrals(records, facIds, includeInternal) {
  if (!Array.isArray(records)) return [];
  return records.filter(r => {
    const data = f(r);
    if (facIds?.size && !rowMatchesFacilityScope(data, 'referrals', facIds, DIRECTION_INBOUND)) {
      return false;
    }
    if (!includeInternal && isSelfReferral(data)) return false;
    return true;
  });
}

/** Index schedule events by referral_id → earliest consult / procedure datetimes. */
export function indexMilestones(eventRecords, consultAliases, procedureAliases) {
  const byRef = new Map();
  let eventCount = 0;
  for (const row of eventRecords || []) {
    const data = f(row);
    const refId = normalizeId(data.referral_id);
    if (!refId) continue;
    const name = String(data.event_name || '').trim();
    if (!name) continue;
    eventCount += 1;
    const at = parseEventDate(data);
    if (!at) continue;
    let pair = byRef.get(refId);
    if (!pair) {
      pair = { consultAt: null, procedureAt: null };
      byRef.set(refId, pair);
    }
    if (matchesAlias(name, consultAliases)) {
      if (!pair.consultAt || at < pair.consultAt) pair.consultAt = at;
    }
    if (matchesAlias(name, procedureAliases)) {
      if (!pair.procedureAt || at < pair.procedureAt) pair.procedureAt = at;
    }
  }
  return { byRef, eventCount };
}

/**
 * Source mix heuristic (no dedicated source API):
 * - Quick Refer: referral_type === 3
 * - External / non-VE: is_referred_by_non_vital_user
 * - Direct: remainder
 */
export function classifySource(data) {
  if (isQuickReferral(data)) return 'quick';
  const nonVe = data.is_referred_by_non_vital_user;
  if (nonVe === 1 || nonVe === '1' || nonVe === true || nonVe === 'true') return 'external';
  return 'direct';
}

export function computeReferralsTab({
  referralRecords,
  priorReferralRecords,
  eventRecords,
  facIds,
  includeInternal,
  consultAliases,
  procedureAliases,
}) {
  const scoped = filterScopedReferrals(referralRecords, facIds, includeInternal);
  const priorScoped = filterScopedReferrals(priorReferralRecords, facIds, includeInternal);
  const internalCount = (referralRecords || []).filter(r => {
    const data = f(r);
    if (facIds?.size && !rowMatchesFacilityScope(data, 'referrals', facIds, DIRECTION_INBOUND)) {
      return false;
    }
    return isSelfReferral(data);
  }).length;

  const { byRef, eventCount } = indexMilestones(
    eventRecords,
    consultAliases,
    procedureAliases
  );

  let consults = 0;
  let procedures = 0;
  const daysToConsult = [];
  const daysConsultToProc = [];

  for (const r of scoped) {
    const rid = referralId(r);
    const receivedAt = recordReceivedAt(r);
    const pair = rid ? byRef.get(rid) : null;
    if (pair?.consultAt) {
      consults += 1;
      const d = daysBetween(receivedAt, pair.consultAt);
      if (d != null && d >= 0) daysToConsult.push(d);
    }
    if (pair?.procedureAt) {
      procedures += 1;
      if (pair.consultAt) {
        const d = daysBetween(pair.consultAt, pair.procedureAt);
        if (d != null && d >= 0) daysConsultToProc.push(d);
      }
    }
  }

  // Prior-year conversion for speed delta (consult lag only)
  let priorConsults = 0;
  const priorDaysToConsult = [];
  for (const r of priorScoped) {
    const rid = referralId(r);
    const receivedAt = recordReceivedAt(r);
    const pair = rid ? byRef.get(rid) : null;
    if (pair?.consultAt) {
      priorConsults += 1;
      const d = daysBetween(receivedAt, pair.consultAt);
      if (d != null && d >= 0) priorDaysToConsult.push(d);
    }
  }

  const received = scoped.length;
  const mix = { direct: 0, quick: 0, external: 0 };
  for (const r of scoped) {
    mix[classifySource(f(r))] += 1;
  }

  const avgConsult = average(daysToConsult);
  const medConsult = median(daysToConsult);
  const avgProc = average(daysConsultToProc);
  const medProc = median(daysConsultToProc);
  const priorAvgConsult = average(priorDaysToConsult);
  let consultSpeedDelta = null;
  if (avgConsult != null && priorAvgConsult != null) {
    consultSpeedDelta = avgConsult - priorAvgConsult; // negative = faster
  }

  return {
    received,
    priorReceived: priorScoped.length,
    internalCount,
    consults,
    procedures,
    consultRate: received ? consults / received : 0,
    procedureRate: received ? procedures / received : 0,
    priorConsults,
    priorConsultRate: priorScoped.length ? priorConsults / priorScoped.length : 0,
    mix,
    avgDaysToConsult: avgConsult,
    medianDaysToConsult: medConsult,
    avgDaysConsultToProc: avgProc,
    medianDaysConsultToProc: medProc,
    consultSpeedDelta,
    eventCount,
    hasEvents: eventCount > 0,
  };
}

export function parseAliasInput(text) {
  return String(text || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

export function formatDays(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)} days`;
}
